import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Immutable from "immutable";
import { Trans, useTranslation } from "react-i18next";
import { useStore } from "stores/connect";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { getConfig } from "components/configurations/helpers";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Button, Row, Col, BootstrapModalForm, Input } from "components/bootstrap";
import { IfPermitted, ISODurationInput } from "components/common";
import Spinner from "components/common/Spinner";
import Select from "components/common/Select/Select";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import "moment-duration-format";
import TimeRangePresetForm from "components/configurations/TimeRangePresetForm";
import generateId from "logic/generateId";
import TimeRangePresetOptionsSummary from "components/configurations/TimeRangePresetOptionSummary";
import { onInitializingTimerange } from "views/components/TimerangeForForm";
import useUserDateTime from "hooks/useUserDateTime";
import { normalizeFromSearchBarForBackend } from "views/logic/queries/NormalizeTimeRange";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useMinimumRefreshInterval from "views/hooks/useMinimumRefreshInterval";
import Alert from "components/bootstrap/Alert";
import ReadableDuration from "components/common/ReadableDuration";
import TimeRangeOptionsForm from "./TimeRangeOptionsForm";
import TimeRangeOptionsSummary from "./TimeRangeOptionsSummary";
const queryTimeRangeLimitValidator = (milliseconds) => milliseconds >= 1;
const surroundingTimeRangeValidator = (milliseconds) => milliseconds >= 1;
const autoRefreshTimeRangeValidator = (minimumRefreshIntervalMS) => (milliseconds) => milliseconds >= 1e3 && milliseconds >= minimumRefreshIntervalMS;
const splitStringList = (stringList) => stringList.split(",").map((f) => f.trim()).filter((f) => f.length > 0);
const buildTimeRangeOptions = (options) => Object.keys(options).map((key) => ({ period: key, description: options[key] }));
const mapQuickAccessBEData = (items, formatTime) => Immutable.List(
  items.map(({ timerange, description, id }) => {
    const presetId = id != null ? id : generateId();
    return { description, id: presetId, timerange: onInitializingTimerange(timerange, formatTime) };
  })
);
const SearchesConfig = () => {
  const { t } = useTranslation("configurations");
  const { userTimezone, formatTime } = useUserDateTime();
  const isLimitEnabled = (config) => moment.duration(config == null ? void 0 : config.query_time_range_limit).asMilliseconds() > 0;
  const { data: minimumRefreshInterval, isInitialLoading: isLoadingMinimumRefreshInterval } = useMinimumRefreshInterval();
  const minimumRefreshIntervalMS = moment.duration(minimumRefreshInterval).asMilliseconds();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(void 0);
  const [formConfig, setFormConfig] = useState(void 0);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const [relativeTimeRangeOptionsUpdate, setRelativeTimeRangeOptionsUpdate] = useState(
    void 0
  );
  const [surroundingTimeRangeOptionsUpdate, setSurroundingTimeRangeOptionsUpdate] = useState(
    void 0
  );
  const [autoRefreshTimeRangeOptionsUpdate, setAutoRefreshTimeRangeOptionsUpdate] = useState(
    void 0
  );
  const [surroundingFilterFieldsUpdate, setSurroundingFilterFieldsUpdate] = useState(void 0);
  const [analysisDisabledFieldsUpdate, setAnalysisDisabledFieldsUpdate] = useState(void 0);
  const [defaultAutoRefreshOptionUpdate, setDefaultAutoRefreshOptionUpdate] = useState(void 0);
  const [timeRangePresetsUpdated, setTimeRangePresetsUpdated] = useState(void 0);
  const [showCancelAfterSeconds, setShowCancelAfterSeconds] = useState(false);
  const sendTelemetry = useSendTelemetry("search");
  useEffect(() => {
    ConfigurationsActions.list(ConfigurationType.SEARCHES_CLUSTER_CONFIG).then(() => {
      const config = getConfig(ConfigurationType.SEARCHES_CLUSTER_CONFIG, configuration);
      setViewConfig(config);
      setFormConfig(config);
      setShowCancelAfterSeconds(!!(config == null ? void 0 : config.cancel_after_seconds));
    });
  }, [configuration]);
  const onUpdate = (field) => (newOptions) => {
    setFormConfig({ ...formConfig, [field]: newOptions });
  };
  const onTimeRangePresetsUpdate = (data) => {
    setTimeRangePresetsUpdated(data);
  };
  const onSurroundingTimeRangeOptionsUpdate = (data) => {
    setSurroundingTimeRangeOptionsUpdate(data);
  };
  const onAutoRefreshTimeRangeOptionsUpdate = (data) => {
    setAutoRefreshTimeRangeOptionsUpdate(data);
  };
  const onFilterFieldsUpdate = (e) => {
    setSurroundingFilterFieldsUpdate(e.target.value);
  };
  const onAnalysisDisabledFieldsUpdate = (e) => {
    setAnalysisDisabledFieldsUpdate(e.target.value);
  };
  const onAutoRefreshDefaultOptionsUpdate = (data) => {
    setDefaultAutoRefreshOptionUpdate(data);
  };
  const onChecked = () => {
    let queryTimeRangeLimit;
    if (isLimitEnabled(formConfig)) {
      queryTimeRangeLimit = "PT0S";
    } else {
      queryTimeRangeLimit = "P30D";
    }
    setFormConfig({ ...formConfig, query_time_range_limit: queryTimeRangeLimit });
  };
  const onCancelAfterSecondsChanged = ({ target: { value } }) => {
    setFormConfig({ ...formConfig, cancel_after_seconds: Number(value) });
  };
  const onCheckedCancelAfterSeconds = () => {
    let cancelAfterSeconds;
    if (showCancelAfterSeconds) {
      cancelAfterSeconds = null;
    } else {
      cancelAfterSeconds = 30;
    }
    setShowCancelAfterSeconds((cur) => !cur);
    setFormConfig({ ...formConfig, cancel_after_seconds: cancelAfterSeconds });
  };
  const openModal = () => {
    setShowConfigModal(true);
  };
  const resetFormUpdates = () => {
    setRelativeTimeRangeOptionsUpdate(void 0);
    setSurroundingTimeRangeOptionsUpdate(void 0);
    setSurroundingFilterFieldsUpdate(void 0);
    setAnalysisDisabledFieldsUpdate(void 0);
    setAutoRefreshTimeRangeOptionsUpdate(void 0);
    setDefaultAutoRefreshOptionUpdate(void 0);
    setTimeRangePresetsUpdated(void 0);
  };
  const handleModalCancel = () => {
    setShowConfigModal(false);
    setFormConfig(viewConfig);
    resetFormUpdates();
  };
  const saveConfig = () => {
    var _a, _b;
    const update = { ...formConfig };
    sendTelemetry(TELEMETRY_EVENT_TYPE.CONFIGURATIONS.SEARCHES_UPDATED, {
      app_action_value: "configuration-save"
    });
    if (relativeTimeRangeOptionsUpdate) {
      update.relative_timerange_options = {};
      relativeTimeRangeOptionsUpdate.forEach((entry) => {
        update.relative_timerange_options[entry.period] = entry.description;
      });
      setRelativeTimeRangeOptionsUpdate(void 0);
    }
    if (timeRangePresetsUpdated) {
      update.quick_access_timerange_presets = timeRangePresetsUpdated.toArray().map(({ description, timerange, id }) => ({
        description,
        timerange: normalizeFromSearchBarForBackend(timerange, userTimezone),
        id
      }));
      setTimeRangePresetsUpdated(void 0);
    }
    if (surroundingTimeRangeOptionsUpdate) {
      update.surrounding_timerange_options = {};
      surroundingTimeRangeOptionsUpdate.forEach((entry) => {
        update.surrounding_timerange_options[entry.period] = entry.description;
      });
      setSurroundingTimeRangeOptionsUpdate(void 0);
    }
    if (surroundingFilterFieldsUpdate) {
      update.surrounding_filter_fields = splitStringList(surroundingFilterFieldsUpdate);
      setSurroundingFilterFieldsUpdate(void 0);
    }
    if (analysisDisabledFieldsUpdate) {
      update.analysis_disabled_fields = splitStringList(analysisDisabledFieldsUpdate);
      setAnalysisDisabledFieldsUpdate(void 0);
    }
    if (autoRefreshTimeRangeOptionsUpdate) {
      update.auto_refresh_timerange_options = Object.fromEntries(
        autoRefreshTimeRangeOptionsUpdate.map((entry) => [entry.period, entry.description])
      );
      setAutoRefreshTimeRangeOptionsUpdate(void 0);
    }
    const defaultAutoRefreshOption2 = defaultAutoRefreshOptionUpdate ? (_a = update.auto_refresh_timerange_options[defaultAutoRefreshOptionUpdate]) != null ? _a : Object.keys(update.auto_refresh_timerange_options)[0] : (_b = update.auto_refresh_timerange_options[update.default_auto_refresh_option]) != null ? _b : Object.keys(update.auto_refresh_timerange_options)[0];
    if (update.default_auto_refresh_option !== defaultAutoRefreshOption2) {
      update.default_auto_refresh_option = defaultAutoRefreshOptionUpdate;
      setDefaultAutoRefreshOptionUpdate(void 0);
    }
    const newFormConfig = { ...formConfig, ...update };
    ConfigurationsActions.update(ConfigurationType.SEARCHES_CLUSTER_CONFIG, newFormConfig).then(() => {
      setShowConfigModal(false);
      resetFormUpdates();
    });
  };
  const timeRangePresetsFromBE = useMemo(
    () => {
      var _a;
      return mapQuickAccessBEData((_a = formConfig == null ? void 0 : formConfig.quick_access_timerange_presets) != null ? _a : [], formatTime);
    },
    [formConfig == null ? void 0 : formConfig.quick_access_timerange_presets, formatTime]
  );
  const timeRangePresets = useMemo(
    () => timeRangePresetsUpdated != null ? timeRangePresetsUpdated : timeRangePresetsFromBE,
    [timeRangePresetsFromBE, timeRangePresetsUpdated]
  );
  if (!viewConfig) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const duration = (config) => moment.duration(config.query_time_range_limit);
  const limit = (config) => isLimitEnabled(config) ? `${config.query_time_range_limit} (${duration(config).humanize()})` : t("shared.disabled");
  const autoRefreshOptions = (config) => autoRefreshTimeRangeOptionsUpdate != null ? autoRefreshTimeRangeOptionsUpdate : buildTimeRangeOptions(config.auto_refresh_timerange_options);
  const formDefaultAutoRefreshOptionUpdate = (config) => defaultAutoRefreshOptionUpdate != null ? defaultAutoRefreshOptionUpdate : config.default_auto_refresh_option;
  const defaultAutoRefreshOption = (config) => {
    var _a;
    return autoRefreshOptions(config).find((option) => option.period === formDefaultAutoRefreshOptionUpdate(config)) ? formDefaultAutoRefreshOptionUpdate(config) : (_a = autoRefreshOptions[0]) == null ? void 0 : _a.period;
  };
  const cancellationTimeout = (config) => config.cancel_after_seconds ? t("searches.cancel_after_seconds", { count: config.cancel_after_seconds }) : t("shared.disabled");
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("searches.title")), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, t("searches.query_time_range_limit_view")), /* @__PURE__ */ React.createElement("dd", null, limit(viewConfig)), /* @__PURE__ */ React.createElement("dd", null, t("searches.query_time_range_limit_description"))), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, t("searches.cancellation_timeout")), /* @__PURE__ */ React.createElement("dd", null, cancellationTimeout(viewConfig)), /* @__PURE__ */ React.createElement("dd", null, t("searches.cancellation_timeout_description"))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement("strong", null, t("searches.time_range_presets")), /* @__PURE__ */ React.createElement(TimeRangePresetOptionsSummary, { options: timeRangePresetsFromBE.toArray() }), /* @__PURE__ */ React.createElement("strong", null, t("searches.surrounding_time_range_options")), /* @__PURE__ */ React.createElement(TimeRangeOptionsSummary, { options: viewConfig.surrounding_timerange_options })), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement(Row, { style: { marginBottom: 20 } }, /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement("strong", null, t("searches.surrounding_filter_fields")), /* @__PURE__ */ React.createElement("ul", null, viewConfig.surrounding_filter_fields && viewConfig.surrounding_filter_fields.map((f) => /* @__PURE__ */ React.createElement("li", { key: f }, f))))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement("strong", null, t("searches.analysis_disabled_fields_view")), /* @__PURE__ */ React.createElement("ul", null, viewConfig.analysis_disabled_fields && viewConfig.analysis_disabled_fields.map((f) => /* @__PURE__ */ React.createElement("li", { key: f }, f)))))), /* @__PURE__ */ React.createElement(Col, { md: 4 }, /* @__PURE__ */ React.createElement("strong", null, t("searches.auto_refresh_options")), /* @__PURE__ */ React.createElement(TimeRangeOptionsSummary, { options: viewConfig.auto_refresh_timerange_options }), /* @__PURE__ */ React.createElement("strong", null, t("searches.default_auto_refresh_interval")), /* @__PURE__ */ React.createElement(
    TimeRangeOptionsSummary,
    {
      options: {
        [viewConfig.default_auto_refresh_option]: viewConfig.auto_refresh_timerange_options[viewConfig.default_auto_refresh_option]
      }
    }
  ))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, t("shared.edit_configuration"))), showConfigModal && formConfig && /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      bsSize: "large",
      title: t("searches.update_title"),
      onSubmitForm: saveConfig,
      onCancel: handleModalCancel,
      submitButtonText: t("shared.update_configuration")
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement("label", { htmlFor: "query-limit-checkbox" }, t("searches.query_time_range_limit_form")), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "query-limit-checkbox",
        type: "checkbox",
        label: t("searches.enable_query_limit"),
        name: "enabled",
        checked: isLimitEnabled(formConfig),
        onChange: onChecked
      }
    ), isLimitEnabled(formConfig) && /* @__PURE__ */ React.createElement(
      ISODurationInput,
      {
        id: "query-timerange-limit-field",
        duration: formConfig.query_time_range_limit,
        update: onUpdate("query_time_range_limit"),
        label: t("searches.query_time_range_limit_label"),
        help: t("searches.query_time_range_limit_help"),
        validator: queryTimeRangeLimitValidator,
        required: true
      }
    ), /* @__PURE__ */ React.createElement("label", { htmlFor: "cancel_after_seconds_checkbox" }, t("searches.query_cancellation_timeout")), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cancel_after_seconds_checkbox",
        type: "checkbox",
        label: t("searches.enable_cancellation_timeout"),
        name: "cancel_after_seconds_checkbox",
        checked: showCancelAfterSeconds,
        onChange: onCheckedCancelAfterSeconds,
        help: t("searches.cancellation_timeout_description")
      }
    ), showCancelAfterSeconds && /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cancel_after_seconds",
        type: "number",
        label: t("searches.cancellation_timeout"),
        name: "cancel_after_seconds",
        min: "1",
        step: "1",
        pattern: "\\d+",
        required: true,
        value: formConfig.cancel_after_seconds,
        onChange: onCancelAfterSecondsChanged
      }
    ), /* @__PURE__ */ React.createElement(TimeRangePresetForm, { options: timeRangePresets, onUpdate: onTimeRangePresetsUpdate }), /* @__PURE__ */ React.createElement(
      TimeRangeOptionsForm,
      {
        options: surroundingTimeRangeOptionsUpdate || buildTimeRangeOptions(formConfig.surrounding_timerange_options),
        update: onSurroundingTimeRangeOptionsUpdate,
        validator: surroundingTimeRangeValidator,
        title: t("searches.surrounding_options_title"),
        help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "searches.surrounding_options_help", components: { strong: /* @__PURE__ */ React.createElement("strong", null) } }))
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "filter-fields-input",
        type: "text",
        label: t("searches.surrounding_filter_fields"),
        onChange: onFilterFieldsUpdate,
        value: surroundingFilterFieldsUpdate || formConfig.surrounding_filter_fields && formConfig.surrounding_filter_fields.join(", "),
        help: t("searches.surrounding_filter_fields_help"),
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "disabled-fields-input",
        type: "text",
        label: t("searches.analysis_disabled_fields"),
        onChange: onAnalysisDisabledFieldsUpdate,
        value: analysisDisabledFieldsUpdate || formConfig.analysis_disabled_fields && formConfig.analysis_disabled_fields.join(", "),
        help: t("searches.analysis_disabled_fields_help"),
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      TimeRangeOptionsForm,
      {
        options: autoRefreshOptions(formConfig),
        update: onAutoRefreshTimeRangeOptionsUpdate,
        validator: autoRefreshTimeRangeValidator(minimumRefreshIntervalMS),
        title: t("searches.auto_refresh_options_title"),
        help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "searches.auto_refresh_options_help", components: { strong: /* @__PURE__ */ React.createElement("strong", null) } }))
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        label: t("searches.default_auto_refresh_option"),
        id: "default-auto-refresh-option",
        required: true,
        help: t("searches.default_auto_refresh_option_help")
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          placeholder: t("searches.select_default_interval"),
          clearable: false,
          options: autoRefreshOptions(formConfig),
          displayKey: "description",
          valueKey: "period",
          onChange: onAutoRefreshDefaultOptionsUpdate,
          value: defaultAutoRefreshOption(formConfig)
        }
      )
    ), !isLoadingMinimumRefreshInterval && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "searches.minimum_refresh_interval_notice",
        values: { interval: minimumRefreshInterval },
        components: { duration: /* @__PURE__ */ React.createElement(ReadableDuration, { duration: minimumRefreshInterval }) }
      }
    )))
  ));
};
export default SearchesConfig;
